import React, { useEffect, useState } from "react";
import { CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, RefreshCw } from "lucide-react";
import { getAllTasks } from "@/services/assuranceActionsService";
import "./widgets.css";
import { useTenancy } from "@/contexts/TenancyContext";
import { useWidgetStatusReporter } from "@/contexts/DashboardWidgetContext";

interface TaskAssignment {
  name: string;
  tasks: number;
  userId: string;
}

const TaskAssignmentListWidget: React.FC = () => {
  const {activeTenancy, userIdToName} = useTenancy();
  const [assignments, setAssignments] = useState<TaskAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Report widget status to dashboard context
  useWidgetStatusReporter('task-assignment-list', isLoading, error, assignments.length > 0);

  const fetchTaskAssignments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch all tasks
      const tasks = await getAllTasks(activeTenancy?.id);
      
      // Group tasks by assignee and count total tasks
      const assignmentsMap = new Map<string, { total: number; name: string }>();
      
      tasks.forEach(task => {
        console.log('[TaskAssignmentListWidget] Task:', task)
        if (!task.checkDetail) return;
        
        // Get assignee info - handle both Unassigned and Deleted User cases consistently
        let assigneeId = task.checkDetail.assignedTo || '';
        let assigneeName = userIdToName[assigneeId] || '';
        
        // If assigneeId is empty, name resolves to empty/Deleted User, or name equals the userId (deleted user fallback), normalize to 'Unassigned'
        if (!assigneeId || !assigneeName || assigneeName === 'Deleted User' || assigneeName === assigneeId) {
          assigneeId = 'Unassigned';
          assigneeName = 'Unassigned';
        }
        
        if (!assignmentsMap.has(assigneeId)) {
          assignmentsMap.set(assigneeId, {
            name: assigneeName,
            total: 0
          });
        }
        
        const assignment = assignmentsMap.get(assigneeId)!;
        assignment.total++;
      });
      
      // Convert map to array and sort by total tasks (descending)
      const assignmentsArray = Array.from(assignmentsMap.entries()).map(([userId, data]) => ({
        userId,
        name: data.name,
        tasks: data.total
      })).sort((a, b) => b.tasks - a.tasks);
      
      setAssignments(assignmentsArray);
    } catch (err) {
      console.error('Failed to fetch task assignments:', err);
      setError('Failed to load task assignments. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    if (!activeTenancy || !activeTenancy.id || ["", "Loading..."]?.includes(activeTenancy.id)) return;
    fetchTaskAssignments();
  }, [activeTenancy]);
  const handleRefresh = () => {
    fetchTaskAssignments();
  };

  return (
    <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size', padding: 'min(2cqw, 1rem)' }}>
      <div className="w-full flex justify-between items-center mb-2">
        <h3 className="text-lg font-semibold">Task Assignment List</h3>
        <button 
          onClick={handleRefresh}
          disabled={isLoading}
          className="p-1 rounded-full hover:bg-muted transition-colors"
          aria-label="Refresh task assignments"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error} <button onClick={fetchTaskAssignments} className="underline ml-2">Retry</button>
          </AlertDescription>
        </Alert>
      )}
      
      <div className="w-full h-full flex flex-col gap-3 flex-1 overflow-auto" style={{ maxHeight: 'calc(100% - 40px)' }}>
        {isLoading ? (
          // Loading skeleton
          Array(4).fill(0).map((_, idx) => (
            <div key={idx} className="flex flex-col gap-2 p-3 bg-muted/40 rounded-lg">
              <Skeleton className="h-5 w-32" />
              <div className="flex items-center gap-4">
                <Skeleton className="h-4 w-20" />
                <div className="flex-1">
                  <Skeleton className="h-2 w-full rounded-full" />
                </div>
              </div>
            </div>
          ))
        ) : assignments.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            No task assignments found
          </div>
        ) : (
          assignments.map((user) => (
            <div
              key={user.userId}
              className="flex flex-row items-center justify-between bg-muted/40 rounded-lg p-3 shadow-sm min-h-[56px]"
              style={{ minHeight: 56 }}
            >
              <div className="font-medium text-base">{user.name}</div>
              <div className="text-sm text-muted-foreground">
                {user.tasks} {user.tasks === 1 ? 'task' : 'tasks'}
              </div>
            </div>
          ))
        )}
      </div>
    </CardContent>
  );
};

export default TaskAssignmentListWidget;
